import { useEffect, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { Mode } from 'constants/index'
import {
  clearPackageMapping,
  copyPackageValues,
  getAutoPackageMappings,
  getPackageMappingKey,
  getPackagesToAppend,
  getReturnMapping,
  getRoutingStops,
  hasNoGoods,
  isBlankPackageMapping,
  isOneOfPackageMappings,
  stopKeyToSequence,
  toStopKeys,
  TPackageMapping,
} from 'helpers/packageAutoPopulate.helpers'
import usePrevious from 'helpers/usePrevious'
import { isGeneratedReturnStop } from 'helpers/waitAndReturn.helpers'
import { TBookingAddressForm, TBookingForm, TBookingPackageForm } from 'types/form-types'

interface IParams {
  mode: number | undefined
  defaultPackage: TBookingPackageForm
}

// Pre-populates consignments from the booking's stops.
// - Runs when the set of stops changes, never on address text edits.
// - Only generates mappings that involve a stop added since the previous render, so a consignment
//   the user removed does not come back. On the first run every stop counts as added.
// - Never rewrites a consignment the user can see, except that on a new booking a stop that leaves
//   several collections and several deliveries clears the pairings pre-filled before it.
// - For a customer with optional Package Details, does nothing: when such a customer is selected,
//   the stops pre-filled on untouched consignments are cleared again.
// - Writes packages with setValue, which works before and after the field array has mounted.
const usePackageAutoPopulate = (params: IParams) => {
  const { mode, defaultPackage } = params

  const {
    getValues,
    setValue,
    formState: { defaultValues },
  } = useFormContext<TBookingForm>()

  const [collectionAddresses, deliveryAddresses, isPackageDetailsSectionOptional] = useWatch({
    name: ['collectionAddresses', 'deliveryAddresses', 'isPackageDetailsSectionOptional'],
  })
  const canAddConsignments = !isPackageDetailsSectionOptional
  const previousCanAddConsignments = usePrevious<boolean>(canAddConsignments)

  const stopsKey = useMemo(
    () => [...toStopKeys(collectionAddresses), ...toStopKeys(deliveryAddresses)].join(','),
    [collectionAddresses, deliveryAddresses],
  )
  const previousStopsKey = usePrevious<string>(stopsKey)

  useEffect(() => {
    const collections = getValues('collectionAddresses') ?? []
    const deliveries = getValues('deliveryAddresses') ?? []

    if (!canAddConsignments) {
      // Optional Package Details: undo the pre-fill on consignments the user has not touched.
      if (mode === Mode.Create && previousCanAddConsignments !== false) {
        const packages = getValues('packages') ?? []
        const cleared = packages.map((pkg) =>
          hasNoGoods(pkg) && !isBlankPackageMapping(pkg) ? clearPackageMapping(pkg) : pkg,
        )

        if (cleared.some((pkg, index) => pkg !== packages[index])) {
          setValue('packages', cleared, { shouldDirty: true })
        }
      }
      return
    }

    // A change of customer can switch Package Details from optional to required; the stops are
    // then treated as new so the consignments get pre-filled.
    const isFirstRun = previousStopsKey === undefined || previousCanAddConsignments === false
    const currentStopKeys = stopsKey ? stopsKey.split(',') : []
    const previousStopKeys = previousStopsKey ? previousStopsKey.split(',') : []
    const currentSequences = currentStopKeys.map(stopKeyToSequence)
    const addedSequences = (
      isFirstRun
        ? currentStopKeys
        : currentStopKeys.filter((stopKey) => !previousStopKeys.includes(stopKey))
    ).map(stopKeyToSequence)
    const removedSequences = previousStopKeys
      .map(stopKeyToSequence)
      .filter((sequence) => !currentSequences.includes(sequence))

    const involvesAddedStop = (mapping: TPackageMapping) =>
      addedSequences.includes(Number(mapping.collectionSequenceOrder)) ||
      addedSequences.includes(Number(mapping.deliverySequenceOrder))

    // Saved booking: nothing changes on load. Only stops added on this page (they have no id yet)
    // get a consignment, which is appended; existing consignments are never filled in or cleared.
    // The loaded stops replace the form defaults after the first render, so they count as added but
    // already have an id.
    if (mode !== Mode.Create) {
      if (isFirstRun) {
        return
      }

      const packages = getValues('packages') ?? []
      const routing = getRoutingStops(collections, deliveries)
      const returnMapping = getReturnMapping(collections, deliveries)
      const returnStopsGeneratedHere = [...collections, ...deliveries]
        .filter(isGeneratedReturnStop)
        .some((address) => !address.id)

      const unsavedSequences = [...routing.collections, ...routing.deliveries]
        .filter((address) => !address.id)
        .map((address) => address.sequenceOrder)
      const involvesStopAddedHere = (mapping: TPackageMapping) =>
        [mapping.collectionSequenceOrder, mapping.deliverySequenceOrder].some(
          (sequence) => addedSequences.includes(sequence) && unsavedSequences.includes(sequence),
        )

      const newMappings = getAutoPackageMappings(routing.collections, routing.deliveries).filter(
        involvesStopAddedHere,
      )
      const newReturnMappings =
        returnMapping && returnStopsGeneratedHere && involvesAddedStop(returnMapping)
          ? [returnMapping]
          : []

      const toAppend = getPackagesToAppend(newMappings, packages)
      const returnToAppend = getPackagesToAppend(newReturnMappings, packages)

      if (!toAppend.length && !returnToAppend.length) {
        return
      }

      setValue(
        'packages',
        [
          ...packages,
          ...toAppend.map((mapping) => ({ ...defaultPackage, ...mapping })),
          ...returnToAppend.map((mapping) => ({
            ...defaultPackage,
            ...copyPackageValues(packages[0]),
            ...mapping,
          })),
        ],
        { shouldDirty: true },
      )
      return
    }

    const packages = getValues('packages') ?? []
    const routing = getRoutingStops(collections, deliveries)
    const referencesRemoved = (value: number | string) =>
      value !== '' && removedSequences.includes(Number(value))

    // A stop the user adds that leaves several collections and several deliveries (no pairing
    // can be guessed) clears the pairings that were pre-filled for the route before it, so the user
    // allocates them. Goods are kept, and so is any pairing that differs from the pre-filled
    // ones. Stops loaded with the form (Book Again) are part of its default values and never
    // count as added by the user.
    const hasSeveralCollectionsAndDeliveries =
      routing.collections.length > 1 && routing.deliveries.length > 1
    const defaultSequences = [
      ...(defaultValues?.collectionAddresses ?? []),
      ...(defaultValues?.deliveryAddresses ?? []),
    ].map((address) => address?.sequenceOrder)
    const isNotAdded = (address: TBookingAddressForm) =>
      !addedSequences.includes(address.sequenceOrder) ||
      defaultSequences.includes(address.sequenceOrder)
    const guessedMappings =
      hasSeveralCollectionsAndDeliveries && !isFirstRun
        ? getAutoPackageMappings(
            routing.collections.filter(isNotAdded),
            routing.deliveries.filter(isNotAdded),
          )
        : []

    // Removed stops: drop a consignment that lost both stops (the return consignment after an
    // untick), otherwise clear only the missing reference.
    const cleared = packages
      .filter(
        (pkg) =>
          !(
            referencesRemoved(pkg.collectionSequenceOrder) &&
            referencesRemoved(pkg.deliverySequenceOrder)
          ),
      )
      .map((pkg) =>
        referencesRemoved(pkg.collectionSequenceOrder) ||
        referencesRemoved(pkg.deliverySequenceOrder)
          ? {
              ...pkg,
              collectionSequenceOrder: referencesRemoved(pkg.collectionSequenceOrder)
                ? ''
                : pkg.collectionSequenceOrder,
              deliverySequenceOrder: referencesRemoved(pkg.deliverySequenceOrder)
                ? ''
                : pkg.deliverySequenceOrder,
            }
          : pkg,
      )
      .map((pkg) => (isOneOfPackageMappings(pkg, guessedMappings) ? clearPackageMapping(pkg) : pkg))

    const mappings = getAutoPackageMappings(routing.collections, routing.deliveries)
    const returnMapping = getReturnMapping(collections, deliveries)
    const allMappings = returnMapping ? [...mappings, returnMapping] : mappings
    const newMappings = allMappings.filter(involvesAddedStop)
    const toAppend: Array<TPackageMapping> = getPackagesToAppend(newMappings, cleared)

    const returnKey = returnMapping ? getPackageMappingKey(returnMapping) : null
    let remaining = toAppend.filter((mapping) => getPackageMappingKey(mapping) !== returnKey)

    const filledPackages = cleared.map((pkg) => {
      if (!remaining.length || !isBlankPackageMapping(pkg)) {
        return pkg
      }

      const [mapping, ...rest] = remaining
      remaining = rest

      return { ...pkg, ...mapping }
    })

    const appendedPackages = remaining.map((mapping) => ({ ...defaultPackage, ...mapping }))

    // The return consignment starts as a copy of the first consignment's goods.
    const returnPackages =
      returnMapping && toAppend.some((mapping) => getPackageMappingKey(mapping) === returnKey)
        ? [{ ...defaultPackage, ...copyPackageValues(filledPackages[0]), ...returnMapping }]
        : []

    const isUnchanged =
      !appendedPackages.length &&
      !returnPackages.length &&
      filledPackages.length === packages.length &&
      filledPackages.every((pkg, index) => pkg === packages[index])

    if (isUnchanged) {
      return
    }

    setValue('packages', [...filledPackages, ...appendedPackages, ...returnPackages], {
      shouldDirty: true,
    })
  }, [
    mode,
    stopsKey,
    previousStopsKey,
    canAddConsignments,
    previousCanAddConsignments,
    getValues,
    setValue,
    defaultPackage,
    defaultValues,
  ])
}

export default usePackageAutoPopulate
